import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { faVolume } from '@fortawesome/pro-solid-svg-icons/faVolume';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import connect from 'components/lib/connect';
import DropdownMenu from 'components/common/dropdown_menu_v2';
import {
  DEFAULT_CHANNEL_SOUNDS,
  NotificationSoundType,
  SHARED_SOUND_OPTIONS,
  SoundMode,
} from 'actions/notification/lib/notification_constants';
import { EndpointTypes } from 'models/endpoint';
import playNotificationSound from 'actions/notification/lib/play_notification_sound';
import RadioGroup from 'components/common/radio_group';
import SetNotificationSoundMode from 'actions/agent_preferences/set_notification_sound_mode';
import UpdateChannelNotificationSound from 'actions/agent_preferences/update_channel_notification_sound';
import { useExecuteAction } from 'components/hooks/connect_hooks';

const SOUND_OFF = '';

// Built per-render with literal t() calls so the strings stay statically extractable.
function buildSoundModeOptions(t) {
  return [
    { label: t('Basic sound'), value: SoundMode.BASIC },
    { label: t('No sound'), value: SoundMode.NONE },
    { label: t('Per channel sound'), value: SoundMode.PER_CHANNEL },
  ];
}

function buildChannelLabels(t) {
  return {
    [EndpointTypes.CHAT]: t('Chat'),
    [EndpointTypes.CUSTOM_CHANNEL]: t('Custom Channel'),
    [EndpointTypes.INSTAGRAM_COMMENT]: t('Instagram Comment'),
    [EndpointTypes.INSTAGRAM_DIRECT]: t('Instagram Direct'),
    [EndpointTypes.SMS]: t('SMS'),
    [EndpointTypes.FB_MESSENGER]: t('Facebook Messenger'),
    [EndpointTypes.WHATSAPP]: t('WhatsApp'),
    [EndpointTypes.TWITTER]: t('Twitter'),
  };
}

// Translated sound names keyed by value; mirrors SHARED_SOUND_OPTIONS + the "off" option.
function buildSoundLabelByValue(t) {
  return {
    [NotificationSoundType.CLICK]: t('Classic'),
    [NotificationSoundType.AGENT_NOTIFICATION]: t('Basic sound'),
    [NotificationSoundType.BELL_CHIME]: t('Bell chime'),
    [NotificationSoundType.MELODY]: t('Melody'),
    [NotificationSoundType.HAPPY]: t('Happy'),
    [NotificationSoundType.SOFT_BELL]: t('Soft bell'),
    [NotificationSoundType.POP]: t('Pop'),
    [NotificationSoundType.CLICKIES]: t('Clickies'),
    [NotificationSoundType.SOFT_DING]: t('Soft ding'),
    [SOUND_OFF]: t('No sound'),
  };
}

// EMAIL and VOICE are deliberately omitted: their routing events flow
// through non-messaging paths (RoutingChannel.MAIL), which the observer
// gates with isMessagingType — so a per-channel choice for them would
// never actually fire on a real notification.
const CHANNELS_IN_ORDER = Object.freeze([
  EndpointTypes.CHAT,
  EndpointTypes.SMS,
  EndpointTypes.FB_MESSENGER,
  EndpointTypes.WHATSAPP,
  EndpointTypes.INSTAGRAM_DIRECT,
  EndpointTypes.INSTAGRAM_COMMENT,
  EndpointTypes.TWITTER,
  EndpointTypes.CUSTOM_CHANNEL,
]);

const CHANNEL_OPTIONS = Object.freeze([...SHARED_SOUND_OPTIONS, { value: SOUND_OFF, label: 'No sound' }]);

export function resolveCurrentMode({ agentNotificationSound, isCustomizeEnabled }) {
  if (isCustomizeEnabled) return SoundMode.PER_CHANNEL;
  if (agentNotificationSound) return SoundMode.BASIC;
  return SoundMode.NONE;
}

export function ChannelNotificationSounds({ agentNotificationSound, channelSounds, isCustomizeEnabled }) {
  const { t } = useTranslation();
  const executeAction = useExecuteAction();

  const soundModeOptions = buildSoundModeOptions(t);
  const channelLabels = buildChannelLabels(t);
  const soundLabelByValue = buildSoundLabelByValue(t);
  const translatedChannelOptions = CHANNEL_OPTIONS.map(option => ({
    value: option.value,
    label: soundLabelByValue[option.value],
  }));

  const currentMode = resolveCurrentMode({ agentNotificationSound, isCustomizeEnabled });

  const onModeChange = useCallback(
    event => {
      const mode = event.target.value;
      if (mode !== currentMode) executeAction(SetNotificationSoundMode, { mode });
    },
    [currentMode, executeAction]
  );

  const onChannelSelect = useCallback(
    (channel, type) => {
      executeAction(UpdateChannelNotificationSound, { channel, type });
    },
    [executeAction]
  );

  const onPreview = useCallback((channel, currentValue) => {
    // Preview whatever the user has currently selected; fall back to the
    // channel default if they've picked "No sound".
    playNotificationSound(currentValue || DEFAULT_CHANNEL_SOUNDS[channel]);
  }, []);

  return (
    <div>
      <RadioGroup data-aid="soundMode" onChange={onModeChange} options={soundModeOptions} value={currentMode} />

      {currentMode === SoundMode.PER_CHANNEL
        ? CHANNELS_IN_ORDER.map(channel => {
            const savedValue = channel in channelSounds ? channelSounds[channel] : DEFAULT_CHANNEL_SOUNDS[channel];
            // Saved prefs may contain a sound type that has since been removed
            // (e.g. TRIPLE_DING). Fall back to the channel default in that case
            // so DropdownMenu can render — otherwise its ButtonText returns
            // undefined and React throws.
            const isValid = CHANNEL_OPTIONS.some(o => o.value === savedValue);
            const currentValue = isValid ? savedValue : DEFAULT_CHANNEL_SOUNDS[channel];
            const label = channelLabels[channel];
            return (
              <ChannelRow key={channel}>
                <ChannelLabel>{label}</ChannelLabel>
                <PreviewButton
                  aria-label={t('Preview {{channel}} sound', { channel: label })}
                  onClick={() => onPreview(channel, currentValue)}
                  title={t('Preview {{channel}} sound', { channel: label })}
                  type="button"
                >
                  <FontAwesomeIcon icon={faVolume} />
                </PreviewButton>
                <DropdownWrapper>
                  <DropdownMenu
                    allowEmptyValues
                    onSelect={type => onChannelSelect(channel, type)}
                    options={translatedChannelOptions}
                    value={currentValue || SOUND_OFF}
                  />
                </DropdownWrapper>
              </ChannelRow>
            );
          })
        : null}
    </div>
  );
}

ChannelNotificationSounds.propTypes = {
  agentNotificationSound: PropTypes.string,
  channelSounds: PropTypes.object.isRequired,
  isCustomizeEnabled: PropTypes.bool.isRequired,
};

const ChannelNotificationSoundsContainer = connect(mapStateToProps)(ChannelNotificationSounds);

function mapStateToProps({ getProvider }) {
  const preferences = getProvider('agentPreferences').get();

  return {
    agentNotificationSound: preferences.agentNotificationSound,
    channelSounds: preferences.channelNotificationSounds || {},
    isCustomizeEnabled: !!preferences.customizeChannelNotificationSounds,
  };
}

const ChannelRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`;

const ChannelLabel = styled.div`
  flex: 1;
`;

const DropdownWrapper = styled.div`
  width: 160px;

  > * {
    width: 100%;
  }
`;

const PreviewButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--color-gray-light);
  cursor: pointer;
  display: flex;
  height: 24px;
  justify-content: center;
  padding: 0;
  width: 24px;

  &:hover {
    background: var(--color-gray-lighter);
    color: var(--color-text);
  }
`;

export default ChannelNotificationSoundsContainer;
